/**
 * Property Relationship Details Panel
 * 
 * Side panel showing selected property details, dependencies, and dependents.
 */

import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Descriptions,
  List,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Divider
} from 'antd';
import { PropertyRelationshipService } from '../../services/api/propertyRelationshipService';
import {
  PropertyRelationships,
  RelationshipType
} from '../../types/propertyRelationships';
import styles from './PropertyRelationshipDetails.module.css';

const { Title, Text } = Typography;

interface PropertyRelationshipDetailsProps {
  artifactId: string;
  propertyId: string | null;
  propertyKey?: string;
  visible: boolean;
  onClose: () => void;
}

const getRelationshipTypeColor = (type: RelationshipType): string => {
  const colors: Record<RelationshipType, string> = {
    [RelationshipType.CALCULATION]: 'blue',
    [RelationshipType.RATIO]: 'green',
    [RelationshipType.AGGREGATION]: 'orange',
    [RelationshipType.CONDITION]: 'pink',
    [RelationshipType.SWITCH]: 'purple',
    [RelationshipType.INPUT]: 'cyan',
    [RelationshipType.DEFAULT]: 'lime',
    [RelationshipType.GLOBAL_REF]: 'red',
    [RelationshipType.REFERENCED_ARTIFACT]: 'geekblue',
    [RelationshipType.UNKNOWN]: 'default'
  };
  return colors[type] || 'default';
};

const getRelationshipTypeLabel = (type: RelationshipType): string => {
  const labels: Record<RelationshipType, string> = {
    [RelationshipType.CALCULATION]: 'Calculation',
    [RelationshipType.RATIO]: 'Ratio',
    [RelationshipType.AGGREGATION]: 'Aggregation',
    [RelationshipType.CONDITION]: 'Condition',
    [RelationshipType.SWITCH]: 'Switch',
    [RelationshipType.INPUT]: 'Input',
    [RelationshipType.DEFAULT]: 'Default',
    [RelationshipType.GLOBAL_REF]: 'Global Variable',
    [RelationshipType.REFERENCED_ARTIFACT]: 'Reference',
    [RelationshipType.UNKNOWN]: 'Unknown'
  };
  return labels[type] || 'Unknown';
};

export const PropertyRelationshipDetails: React.FC<PropertyRelationshipDetailsProps> = ({
  artifactId,
  propertyId,
  propertyKey,
  visible,
  onClose
}) => {
  const [relationships, setRelationships] = useState<PropertyRelationships | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && propertyId) {
      loadRelationships();
    } else {
      setRelationships(null);
    }
  }, [visible, propertyId, artifactId]);

  const loadRelationships = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const data = await PropertyRelationshipService.getPropertyRelationships(
        artifactId,
        propertyId
      );
      setRelationships(data);
    } catch (error: any) {
      console.error('Error loading property relationships:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Property Details
          </Title>
          {relationships && (
            <Tag color={getRelationshipTypeColor(relationships.relationshipType)}>
              {getRelationshipTypeLabel(relationships.relationshipType)}
            </Tag>
          )}
        </Space>
      }
      placement="right"
      width={500}
      onClose={onClose}
      open={visible}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : relationships ? (
        <div className={styles.propertyRelationshipDetails}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Property Key">
              {relationships.propertyKey}
            </Descriptions.Item>
            <Descriptions.Item label="Relationship Type">
              <Tag color={getRelationshipTypeColor(relationships.relationshipType)}>
                {getRelationshipTypeLabel(relationships.relationshipType)}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Dependencies</Divider>
          {relationships.dependencies.length > 0 ? (
            <List
              size="small"
              dataSource={relationships.dependencies}
              renderItem={(dep) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{dep.propertyKey}</Text>
                        <Tag color={getRelationshipTypeColor(dep.relationshipType)}>
                          {getRelationshipTypeLabel(dep.relationshipType)}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        {dep.formula && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {dep.formula}
                          </Text>
                        )}
                        {dep.calculationType && (
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                            Type: {dep.calculationType}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="No dependencies" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          <Divider orientation="left">Dependents</Divider>
          {relationships.dependents.length > 0 ? (
            <List
              size="small"
              dataSource={relationships.dependents}
              renderItem={(dep) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{dep.propertyKey}</Text>
                        <Tag color={getRelationshipTypeColor(dep.relationshipType)}>
                          {getRelationshipTypeLabel(dep.relationshipType)}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div>
                        {dep.howUsed && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {dep.howUsed}
                          </Text>
                        )}
                        {dep.formula && (
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                            {dep.formula}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="No dependents" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </div>
      ) : (
        <Empty description="No property selected" />
      )}
    </Drawer>
  );
};

